import AccountCircle from '@mui/icons-material/AccountCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import Logout from '@mui/icons-material/Logout';
import { Box, Button, Menu, MenuItem, SxProps, Typography } from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import { ChangePasswordModal, User, changePasswordModalId, useGetAuthQuery, useLogout } from '@vxui/common';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalContext } from '../../contexts';

const userInfoPollingInterval = 30 * 1000;

export const UserDetails: FC<{ sx?: SxProps }> = ({ sx }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  const { data: user, isSuccess } = useGetAuthQuery(undefined, { pollingInterval: userInfoPollingInterval });
  const logout = useLogout();

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const { showModal, hideModal } = useModalContext();
  const closeChangePasswordModal = useCallback(() => hideModal(changePasswordModalId), [hideModal]);
  const openChangePasswordModal = useCallback(
    (user: User) =>
      showModal(changePasswordModalId, <ChangePasswordModal user={user} onClose={closeChangePasswordModal} />),
    [closeChangePasswordModal, showModal],
  );
  const changePassword = useCallback(() => user && openChangePasswordModal(user), [user, openChangePasswordModal]);

  return (
    <Box textAlign="right" sx={sx}>
      {isSuccess && user && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
            <Button
              onClick={handleClick}
              size="small"
              sx={theme => ({
                transition: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  span: {
                    color: theme.palette.primary[70],
                  },
                  svg: {
                    color: theme.palette.primary[60],
                  },
                },
              })}
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <AccountCircle sx={theme => ({ width: 32, height: 32, color: theme.palette.primary[70] })} />
              <Typography
                variant="p14"
                component="span"
                sx={{ marginLeft: '8px', textTransform: 'none', letterSpacing: '1px' }}
              >
                {user?.username ?? ''}
              </Typography>
            </Button>
          </Box>
          <Menu
            anchorEl={anchorEl}
            hideBackdrop={true}
            disablePortal={true}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            sx={{
              '.MuiBackdrop-root': {
                background: 'transparent',
              },
              '.MuiPaper-root': {
                minWidth: '200px',
              },
              '.MuiMenuItem-root': {
                margin: '0px',
                lineHeight: '100%',
                height: 'auto',
              },
            }}
          >
            <MenuItem onClick={changePassword}>
              <ListItemIcon>
                <LockResetIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="p14" component="span" sx={{ textDecoration: 'none' }}>
                {t(`button.changePassword`)}
              </Typography>
            </MenuItem>
            <MenuItem onClick={logout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <Typography variant="p14" component="span" sx={{ textDecoration: 'none' }}>
                {t(`button.logout`)}
              </Typography>
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};
